import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Home as HomeIcon } from 'lucide-react'
import { Section, SectionHeading, IconCircle, CTABand, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL, OFFICE_ADDRESS, CANADIAN_LINE, SERVICE_AREA } from '../lib/constants'
import { submitInquiry } from '../lib/api'

const serviceTitles = [
  'Personal Care Services',
  'Meal Preparation & Light Housekeeping',
  'Companionship & Outings',
  'Post-Hospital Care',
  'Specialized Care',
]
const inquiryTypes = ['Seeking care', 'Seeking a career', 'General question']
const contactMethods = ['Phone call', 'Email', 'Text message']

const labelHint = { fontWeight: 400, color: 'var(--color-muted)' }

function ContactForm() {
  const [f, setF] = useState({
    name: '', email: '', phone: '', postal: '',
    inquiryType: inquiryTypes[0], preferredContact: contactMethods[0],
    message: '', consent: false,
  })
  const [services, setServices] = useState([])
  const [errors, setErrors] = useState({})
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const onField = (e) => {
    setF({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
    setErrors({})
  }
  const toggleService = (title) => {
    setServices(services.includes(title) ? services.filter((x) => x !== title) : [...services, title])
    setErrors({})
  }

  const submit = async () => {
    if (busy) return
    const errs = {}
    if (!f.name.trim()) errs.name = 'Please add your name so we can reply.'
    if (!f.email.trim()) errs.email = 'Please add your email so we can reply.'
    if (!f.consent) errs.consent = "Please check the consent box so we know it's okay to contact you."
    setErrors(errs)
    if (Object.keys(errs).length) return
    const [firstName, ...rest] = f.name.trim().split(' ')
    setBusy(true)
    try {
      await submitInquiry({
        kind: 'general',
        firstName,
        lastName: rest.join(' '),
        email: f.email,
        phone: f.phone,
        postalCode: f.postal,
        whoNeedsCare: '',
        relationship: '',
        services,
        frequency: '',
        assessmentType: '',
        preferredSlots: [],
        preferredContact: f.preferredContact,
        message: '[' + f.inquiryType + ' · Contact via: ' + f.preferredContact + '] ' + f.message,
        consent: true,
      })
      setSent(true)
    } catch (err) {
      setErrors({ submit: err.message })
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <h2 className="h2-section">Thank you — your message is with our team.</h2>
        <p style={{ margin: 0, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
          A care coordinator will reach out using the contact method you chose. If it's urgent, please call {PHONE_DISPLAY} and we'll pick it up right away.
        </p>
        <Link to="/services" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }}>Explore our services</Link>
      </div>
    )
  }

  return (
    <>
      <h2 className="h2-section" style={{ marginBottom: 24 }}>Send us a message</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <label className="field-label" htmlFor="ct-name">Your name *</label>
          <input id="ct-name" className="field-input" name="name" value={f.name} onChange={onField} />
          {errors.name && <p className="field-error">{errors.name}</p>}
        </div>
        <div className="grid grid-2" style={{ gap: 24 }}>
          <div style={{ minWidth: 0 }}>
            <label className="field-label" htmlFor="ct-email">Email *</label>
            <input id="ct-email" className="field-input" name="email" type="email" value={f.email} onChange={onField} />
            {errors.email && <p className="field-error">{errors.email}</p>}
          </div>
          <div style={{ minWidth: 0 }}>
            <label className="field-label" htmlFor="ct-phone">Phone</label>
            <input id="ct-phone" className="field-input" name="phone" type="tel" value={f.phone} onChange={onField} />
          </div>
        </div>
        <div className="grid grid-2" style={{ gap: 24 }}>
          <div style={{ minWidth: 0 }}>
            <label className="field-label" htmlFor="ct-postal">Postal code</label>
            <input id="ct-postal" className="field-input" name="postal" placeholder="So we can confirm your area" value={f.postal} onChange={onField} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label className="field-label" htmlFor="ct-inquiryType">I'm reaching out about</label>
            <select id="ct-inquiryType" className="field-input" name="inquiryType" value={f.inquiryType} onChange={onField}>
              {inquiryTypes.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="field-label">Services you're interested in <span style={labelHint}>— choose as many as apply</span></label>
          <div className="grid grid-2" style={{ gap: 12 }}>
            {serviceTitles.map((title) => {
              const sel = services.includes(title)
              return (
                <button
                  key={title}
                  type="button"
                  onClick={() => toggleService(title)}
                  aria-pressed={sel}
                  style={{
                    display: 'flex', minHeight: 52, alignItems: 'center', gap: 12, borderRadius: 4,
                    border: '1px solid var(--color-line)', background: 'var(--color-panel)', padding: '12px 16px',
                    width: '100%', minWidth: 0, boxSizing: 'border-box',
                    fontSize: 16, fontFamily: 'var(--font-sans)', cursor: 'pointer', textAlign: 'left', color: 'var(--color-ink)',
                  }}
                >
                  <span style={{
                    display: 'flex', height: 24, width: 24, flexShrink: 0, alignItems: 'center', justifyContent: 'center',
                    borderRadius: 4, border: '2px solid var(--color-teal-800)',
                    background: sel ? 'var(--color-teal-800)' : '#FFFFFF',
                    color: 'var(--color-cream)', fontSize: 14, fontWeight: 700,
                  }}>{sel ? '✓' : ''}</span>
                  {title}
                </button>
              )
            })}
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="ct-preferredContact">Preferred contact method</label>
          <select id="ct-preferredContact" className="field-input" name="preferredContact" value={f.preferredContact} onChange={onField}>
            {contactMethods.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="ct-message">How can we help?</label>
          <textarea id="ct-message" className="field-input" name="message" style={{ minHeight: 120 }} value={f.message} onChange={onField} />
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 16, color: 'var(--color-muted)', cursor: 'pointer' }}>
            <input
              type="checkbox" name="consent" checked={f.consent} onChange={onField}
              style={{ width: 24, height: 24, minHeight: 24, marginTop: 2, flexShrink: 0 }}
            />
            I agree that True North Care &amp; Support may contact me about my inquiry.
          </label>
          {errors.consent && <p className="field-error">{errors.consent}</p>}
        </div>
        {errors.submit && <p className="field-error">{errors.submit}</p>}
        <button type="button" onClick={submit} disabled={busy} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
          {busy ? 'Sending…' : 'Send message to our care team'}
        </button>
      </div>
    </>
  )
}

export default function Contact() {
  return (
    <>
      {/* Hero */}
      <Section tone="panel">
        <p className="eyebrow">Contact us</p>
        <h1 className="h1-hero" style={{ marginTop: 16, maxWidth: 768 }}>Let's talk about what would help most at home.</h1>
        <p style={{ margin: '24px 0 0 0', maxWidth: 672, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
          Call us and you'll reach a real person in Calgary. Prefer to write? Send a note below and we'll reply the same business day whenever we can.
        </p>
      </Section>

      {/* Contact details + form */}
      <Section tone="cream">
        <div className="grid" style={{ '--cols': 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: 48, alignItems: 'start' }}>
          <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 32 }}>
            <div className="card-teal">
              <IconCircle icon={HomeIcon} variant="on-teal" />
              <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>Our Calgary office</h3>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 90%)', overflowWrap: 'anywhere' }}>
                <p style={{ margin: 0 }}>{OFFICE_ADDRESS}</p>
                <p style={{ margin: 0 }}>
                  <a href={PHONE_TEL} style={{ color: 'var(--color-cream)', textDecoration: 'underline', textUnderlineOffset: 4 }}>{PHONE_DISPLAY}</a>
                </p>
                <p style={{ margin: 0 }}>{SERVICE_AREA}</p>
                <p style={{ margin: 0 }}>Monday to Friday, 8:30 AM – 5:00 PM. Care support available beyond office hours.</p>
                <p style={{ margin: 0 }}>{CANADIAN_LINE}</p>
              </div>
            </div>
            <div className="card-cream">
              <h2 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>Prefer to talk it through?</h2>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
                We're glad to complete any form with you over the phone, including the assessment request.
              </p>
              <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <a href={PHONE_TEL} className="btn btn-primary">Call {PHONE_DISPLAY}</a>
                <Link to="/schedule-assessment" className="btn btn-secondary">Schedule an In-Home Assessment</Link>
              </div>
            </div>
            <NonMedicalNote />
          </div>
          <div style={{ minWidth: 0, boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--color-line)', background: '#FFFFFF', padding: 'clamp(20px, 5vw, 40px)' }}>
            <ContactForm />
          </div>
        </div>
      </Section>

      {/* What happens next */}
      <Section tone="panel-deep">
        <SectionHeading
          eyebrow="What happens next"
          title="A short conversation, then a plan that fits."
          lede="We listen first, confirm your service area, and offer up to three assessment times that suit your family."
        />
      </Section>

      <CTABand />
    </>
  )
}
